import React, {useEffect, useRef, useState} from "react";
import SearchableText from "./SearchableText";
import classNames from "classnames";
import {ReactComponent as Edit} from '../../assets/edit.svg';
import {useDispatch} from "react-redux";
import {actions} from "../../store";
import {TranscriptionRowInterface, useStyle} from "./TranscriptionRow.utils";
import StartTimeSlot from "./StartTimeSlot";
import {useTranslation} from "react-i18next";
import {timeToSeconds} from "../../services/stringHelpers";
import {usePermissionContext} from "../../hooks/usePermissions";
import {PermissionType} from "../../store/types/Permission";

const TranscriptionRow: React.VFC<TranscriptionRowInterface> = (
    {
        id,
        search,
        start,
        text,
        active,
        matchIndex,
        rowNumber,
        searchMatrix,
        callSegment,
        full,
        moveProgress,
        readOnly = false
    }) => {

    const classes = useStyle();
    const {t} = useTranslation()
    const dispatch = useDispatch();

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const mainContainerRef = useRef<HTMLDivElement | null>(null);

    const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [value, setValue] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [displayText, setDisplayText] = useState<string>( !text.length ? t('screens:calls.silence'): text);
    const [isSilence] = useState<boolean>(!text.length);
    const permissions = usePermissionContext();
    const isReadOnly = permissions != PermissionType.Visible || readOnly;

    const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value);
    };
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value);
    };

    const toggleEditMode = () => {

        if(!isSilence)
        {
            setValue(!isInEditMode ? displayText : '')
            setIsInEditMode(prev => !prev);
        }

    }
    const handleSave = () => {
        setIsSaving(true)

        const callback = (success: boolean) => {
            if (success) {
                setDisplayText(value)
                toggleEditMode();

            }
            setIsSaved(true)
            setIsSaving(false)
        }

        callSegment && dispatch(actions.updateCallRecordingSegment.request({...callSegment, text: value, callback}))
    }

    const searchActive = rowNumber !== undefined && matchIndex !== undefined && !!id
    const notNullMatrix = searchActive && searchMatrix && searchMatrix[rowNumber]?.length
    let index: number | undefined

    const markerId = `highlighted-part`

    if (searchActive) {
        // @ts-ignore
        index = notNullMatrix && searchMatrix[rowNumber].findIndex(el => el === matchIndex)
    }

    const autoResize = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        const element = mainContainerRef.current;
        if (element && !element.contains(event.target as Node)) {
            setIsInEditMode(false);
            setValue('')
        }
    };

    useEffect(() => {
        const textarea = textareaRef.current;
        document.addEventListener('mousedown', handleClickOutside);

        if (textarea) {
            autoResize();
            textarea.addEventListener('input', autoResize);
        }

        return () => {
            textarea && textarea.removeEventListener('input', autoResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };

    }, [value]);

    return (
        <div
            ref={mainContainerRef}
            id={id}
            className={classNames(
                classes.container,
                (active && !isInEditMode && !readOnly) && classes.active,
                (isSilence && !active) && classes.silence,
                isInEditMode && classes.edit,
                full && classes.full
            )}
        >
            {!readOnly && (
                <StartTimeSlot
                    time1={start}
                    active={active && !isInEditMode}
                    interactive={!isSilence}
                    onClick={!isSilence && moveProgress ? () => moveProgress(timeToSeconds(start)) : undefined}
                />
            )}

            {!isInEditMode && (
                <>
                    <SearchableText
                        text={displayText}
                        search={search}
                        bold={active && !readOnly}
                        activeFoundIndex={(searchActive && index !== undefined) ? index : undefined}
                        markedId={markerId}
                    />
                    {!isSilence && !isReadOnly && (<div className={classes.actionContainer}>
                        <Edit onClick={toggleEditMode}/>
                        {isSaved && <span className={classes.edited}>Edited</span>}
                    </div>)}
                </>
            )}

            {isInEditMode && (
                <>
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={handleChange}
                    onInput={handleInput}
                    className={classes.textArea}
                    autoFocus={true}
                    disabled={isSaving}
                />
                    <span className={!isSaving ? classes.save : classes.saveDisabled} onClick={handleSave}>{t('common:save')}</span>
                </>
            )}

        </div>
    )
}

export default TranscriptionRow;